/**
 * Marketing config — single source of truth for the calc page's marketing
 * copy (brand, nav, hero, CTAs, podcast/email row, footer credit) and every
 * outbound URL. Calculator UI text lives with its component, and clinical copy
 * lives in content/clinical-content.md. The PDF deliberately does NOT use any
 * of this (pristine clinical artifact, no marketing pollution).
 *
 * To swap a URL — for example, when the current Foundations cohort sells out
 * and Anthony moves to an evergreen URL — edit this file and redeploy.
 * It's a 30-second change, no other code touches these strings.
 *
 * Single-domain architecture (locked):
 *   - calc.thecranialdoc.com  → where this app deploys (the calc IS the brand surface)
 *   - thecranialdoc.com/*     → all marketing CTAs link here
 *
 * Brand surface = The Cranial Doc. Wordmark, copyright, and credits all
 * reflect that. The clinical PDF (handed to parents) uses a more formal
 * "Dr. Anthony Pellegrino" attribution and lives in clinical-content.md.
 */

export const MARKETING = {
  brand: {
    wordmark: "THE CRANIAL DOC",
    /** Accessible name for the wordmark link back to the home page. */
    homeAriaLabel: "THE CRANIAL DOC — home",
    toolCredit: "Cranial assessment tool by Dr. Anthony & The Cranial Doc™",
  },
  /** Accessible name for the top nav landmark. */
  topNavAriaLabel: "Primary",
  topNav: [
    { label: "PODCAST", url: "https://thecranialdoc.com/podcast" },
    { label: "INSTAGRAM", url: "https://instagram.com/thecranialdoc" },
  ],
  topNavCTA: {
    label: "FOUNDATIONS COURSE",
    // Evergreen training URL — the /founder cohort sold out, so all course
    // CTAs now point at /training (the standing course/waitlist page).
    url: "https://thecranialdoc.com/training",
  },
  hero: {
    title: "Baby Head Shape Calculator",
    subtitle: "Measure CI and CVAI. Generate a parent handout in seconds.",
    credit: "by Dr. Anthony",
    /**
     * Variant A hero card — Foundations promo that lives INSIDE the hero
     * on the right.
     *
     * Constraints: title ≤ 8 words, body ≤ 25 words. sealText is 1–3 short
     * lines that fit inside an 88px circle. CTA links to the same
     * Foundations URL the rest of the page uses.
     */
    foundationsCard: {
      eyebrow: "FOUNDATIONS",
      title: "Learn what to do once you have the findings.",
      // Body copy is the rewrite originally written for the (now-removed)
      // foundations banner body — see brand-voice-pass.md item 42 → 12.
      body: "The clinical foundation for pediatric cranial subluxation correction. Learn the evaluation system, the correction sequence, and the case management approach that turns cranial work into a specialty, not a side skill.",
      ctaLabel: "Enroll →",
      ctaUrl: "https://thecranialdoc.com/training",
      sealText: ["FOUNDATIONS", "FOR CRANIAL", "WORK"],
    },
  },
  inlineCTA: {
    headline: "The calculator gives you the number. The course teaches you what to do with it.",
    label: "EXPLORE FOUNDATIONS COURSE",
    url: "https://thecranialdoc.com/training",
  },
  /** Accessible name for the podcast + email row (screen readers announce it). */
  secondaryRowLabel: "Other resources",
  podcast: {
    title: "The Cranial Doc Podcast",
    linkLabel: "Listen →",
    description:
      "Clinical mastery, practice systems, and the business of doing this work at the highest level.",
    url: "https://thecranialdoc.com/podcast",
  },
  emailOptIn: {
    title: "Email list",
    description: "Get tools like this delivered. New ones every few weeks.",
    label: "JOIN THE LIST",
    // Homepage IS the opt-in form (link-out, no inline form — preserves zero-third-party-runtime rule).
    url: "https://thecranialdoc.com",
  },
  bridgeLink: {
    text: "Want to understand more about your baby's measurements?",
    linkLabel: "littleroots.life",
    // Apex is the parent-learning destination. Strategic shift 2026-05-09: the
    // single page lives at the apex (not the `learn.` subdomain) so traffic
    // from cvai-tools + Little Roots consolidates there as the apex grows
    // into a fuller marketing site (services, find-a-doctor, doctor hub) —
    // and a future WordPress migration only has to touch one DNS record.
    url: "https://littleroots.life",
  },
  footer: {
    links: [{ label: "Privacy", url: "https://thecranialdoc.com/privacy-policy" }],
    toolCredit: "Cranial assessment tool by Dr. Anthony & The Cranial Doc™",
    copyright: "© 2026 THE CRANIAL DOC · THECRANIALDOC.COM",
  },
} as const;

export type Marketing = typeof MARKETING;
